import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { Button } from './ui/button';
import logo from '../assets/logo.png';

const navLinks = [
  { label: 'Home', to: '/' },
  { label: 'Speciality Care', to: '/speciality-services' },
  { label: 'Webinars', to: '/webinars' },
  { label: 'Insights', to: '/blog' },
];

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
      scrolled
        ? 'bg-white/70 backdrop-blur-2xl shadow-lg shadow-black/5'
        : 'bg-white/80 backdrop-blur-xl'
    }`}>
      {/* Liquid glass effect overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-[#1e3a8a]/5 via-transparent to-[#22d3ee]/5 pointer-events-none" />
      {/* Animated border gradient */}
      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-[#22d3ee]/50 to-transparent" />

      <nav className="mx-auto max-w-7xl px-3 sm:px-4 md:px-6 lg:px-8 relative">
        <div className="flex h-16 sm:h-20 items-center justify-between">

          {/* Logo */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center group">
              <img
                src={logo}
                alt="Globe Medical"
                className="h-8 sm:h-10 w-auto object-contain"
                width="160"
                height="40"
              />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex lg:items-center lg:space-x-1">
            {navLinks.map(({ label, to }) => (
              <Link
                key={label}
                to={to}
                className="relative px-5 py-2 text-base font-semibold text-gray-700 hover:text-[#1e3a8a] transition-colors duration-300 group"
              >
                <span className="relative z-10">{label}</span>
                <div className="absolute inset-0 bg-white/40 backdrop-blur-md rounded-full opacity-0 group-hover:opacity-100 scale-75 group-hover:scale-100 transition-all duration-300 border border-white/20" />
              </Link>
            ))}

            {[{ label: 'About Us', to: '/about' }, { label: 'Contact Us', to: '/contact' }].map(({ label, to }) => (
              <Link
                key={label}
                to={to}
                className="relative px-5 py-2 text-base font-semibold text-gray-700 hover:text-[#1e3a8a] transition-colors duration-300 group"
              >
                <span className="relative z-10">{label}</span>
                <div className="absolute inset-0 bg-white/40 backdrop-blur-md rounded-full opacity-0 group-hover:opacity-100 scale-75 group-hover:scale-100 transition-all duration-300 border border-white/20" />
              </Link>
            ))}
          </div>

          {/* Login Button + Mobile toggle */}
          <div className="flex items-center space-x-3">
            <a href="https://globemed.zionitai.com/patientLogin" target="_blank" rel="noopener noreferrer">
              <Button className="relative overflow-hidden bg-gradient-to-r from-[#1e3a8a] to-[#2563eb] hover:from-[#22d3ee] hover:to-[#1e3a8a] text-white px-4 sm:px-6 py-2 sm:py-2.5 rounded-full transition-all duration-300 shadow-lg shadow-[#1e3a8a]/30 hover:shadow-xl hover:shadow-[#22d3ee]/40 hover:scale-105 group">
                <span className="relative z-10 text-sm sm:text-base font-semibold">Login</span>
                <div className="absolute inset-0 bg-white/20 rounded-full scale-0 group-hover:scale-100 transition-transform duration-500 origin-center" />
              </Button>
            </a>

            {/* Mobile menu button */}
            <button
              aria-label={mobileMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
              className="lg:hidden p-2 bg-white/40 backdrop-blur-xl border border-white/20 rounded-full hover:bg-white/60 transition-all duration-300"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="h-6 w-6 text-[#1e3a8a]" /> : <Menu className="h-6 w-6 text-[#1e3a8a]" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-white/20 animate-fadeIn">
            <div className="flex flex-col space-y-2">
              {navLinks.map(({ label, to }) => (
                <Link
                  key={label}
                  to={to}
                  onClick={() => setMobileMenuOpen(false)}
                  className="px-6 py-3 min-h-[44px] text-sm font-medium text-gray-700 hover:text-[#1e3a8a] bg-white/40 backdrop-blur-md rounded-xl hover:bg-white/60 transition-all duration-300 border border-white/20 flex items-center"
                >
                  {label}
                </Link>
              ))}

              {[{ label: 'About Us', to: '/about' }, { label: 'Contact Us', to: '/contact' }].map(({ label, to }) => (
                <Link
                  key={label}
                  to={to}
                  onClick={() => setMobileMenuOpen(false)}
                  className="px-6 py-3 min-h-[44px] text-sm font-medium text-gray-700 hover:text-[#1e3a8a] bg-white/40 backdrop-blur-md rounded-xl hover:bg-white/60 transition-all duration-300 border border-white/20 flex items-center"
                >
                  {label}
                </Link>
              ))}

              <a href="https://globemed.zionitai.com/patientLogin" target="_blank" rel="noopener noreferrer">
                <Button className="mt-2 w-full bg-gradient-to-r from-[#1e3a8a] to-[#2563eb] text-white rounded-full font-semibold min-h-[44px]">
                  Login
                </Button>
              </a>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;
